import React, {useState, useEffect, useRef} from "react"
import SocialLinksNav from "./socialLinksNav"
import * as styles from "./header.module.css"
import {useScrollOpacity} from "../hooks/useScrollOpacity"
import ScrollLink from "./scrollLink"
import {Link} from "gatsby"
import {useIsRootPage} from "../hooks/useIsRootPage"
import {sendGAPageview} from "../utils/analytics"

interface HeaderProps {
	navHighlight?: string
}

export default function Header({navHighlight}: HeaderProps) {
	const isRootPage = useIsRootPage()
	const navRef = useRef<HTMLUListElement>(null)
	const underlineRef = useRef<HTMLDivElement>(null)
	const lastClickTimeRef = useRef<number>(0)
	const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	// Define navigation sections
	const sections = [
		{name: "Home"},
		{name: "About"},
		{name: "Music"},
		{name: "Tools", path: "/wavelength"},
		{name: "Contact"},
	]

	// Visual effects for header
	const heroOpacity = useScrollOpacity()
	const headerOpacity = isRootPage ? 1 - heroOpacity : 1
	const scrolled = heroOpacity < 1 || !isRootPage

	// Track the active section on home page
	const [activeSection, setActiveSection] = useState<string>(() => {
		// Initialize from URL hash if on home page
		if (isRootPage && typeof window !== "undefined") {
			const hash = window.location.hash.slice(1)
			return hash || "Home" // Default to Home if no hash
		}
		return ""
	})

	// Position the underline under the active element
	const positionUnderline = (element: HTMLElement) => {
		if (!underlineRef.current) return

		const {offsetLeft, offsetWidth} = element
		underlineRef.current.style.left = `${offsetLeft}px`
		underlineRef.current.style.width = `${offsetWidth}px`
	}

	// Find active nav element based on current state
	const getActiveNavElement = (): HTMLElement | null => {
		if (!navRef.current) return null

		// On non-root pages, find by navHighlight
		if (!isRootPage && navHighlight) {
			const allLinks = navRef.current.querySelectorAll("a")
			for (const link of Array.from(allLinks)) {
				if (link.textContent === navHighlight) {
					return link as HTMLElement
				}
			}
		}
		// On root page, find by activeSection
		else if (isRootPage && activeSection) {
			const allLinks = navRef.current.querySelectorAll("a")
			for (const link of Array.from(allLinks)) {
				if (link.textContent?.toLowerCase() === activeSection.toLowerCase()) {
					return link as HTMLElement
				}
			}
		}

		return null
	}

	// Update underline position whenever activeSection or navHighlight changes
	useEffect(() => {
		const activeElement = getActiveNavElement()
		if (activeElement) {
			positionUnderline(activeElement)
		}
	}, [activeSection, navHighlight, isRootPage])

	// Check for DOM changes that might affect positioning
	useEffect(() => {
		// Use ResizeObserver to detect when elements are fully rendered
		const observer = new ResizeObserver(() => {
			const activeElement = getActiveNavElement()
			if (activeElement) {
				positionUnderline(activeElement)
			}
		})

		// Observe nav elements
		if (navRef.current) {
			const navItems = navRef.current.querySelectorAll("li")
			navItems.forEach((item) => observer.observe(item))
		}

		return () => observer.disconnect()
	}, [])

	// Handle scroll events to update active section
	const handleScroll = () => {
		if (!isRootPage || navHighlight) return

		// Skip section updates if click was within the last 1000ms (scroll animation time)
		if (Date.now() - lastClickTimeRef.current < 300) return

		const scrollPosition = window.scrollY
		const windowHeight = window.innerHeight
		const scrollSections = sections
			.filter((section) => !section.path)
			.map((section) => section.name)

		let newActiveSection: string

		// At top of page
		if (scrollPosition < 50) {
			newActiveSection = scrollSections[0]
		}
		// At bottom of page
		else if (
			scrollPosition + windowHeight >=
			document.documentElement.scrollHeight - 50
		) {
			newActiveSection = scrollSections[scrollSections.length - 1]
		}
		// Somewhere in the middle
		else {
			newActiveSection =
				scrollSections.findLast((sectionName) => {
					const element = document.getElementById(sectionName.toLowerCase())
					if (!element) return false

					const rect = element.getBoundingClientRect()
					const threshold = window.innerWidth < 700 ? 0.3 : 0.4

					return rect.bottom > 50 && rect.top / windowHeight < threshold
				}) || scrollSections[0]
		}

		if (newActiveSection !== activeSection) {
			setActiveSection(newActiveSection)

			// Update URL and analytics
			if (typeof window !== "undefined") {
				const newPath = `/#${newActiveSection.toLowerCase()}`
				window.history.pushState(null, "", newPath)
				sendGAPageview(
					newActiveSection,
					`/home/${newActiveSection.toLowerCase()}`
				)
			}
		}
	}

	// Set up scroll listener
	useEffect(() => {
		if (typeof window !== "undefined" && isRootPage) {
			window.addEventListener("scroll", handleScroll)
			// Initial call to set correct section
			handleScroll()
			return () => window.removeEventListener("scroll", handleScroll)
		}
	}, [isRootPage, activeSection])

	// Handle mouse interactions
	const handleMouseEnter = (event: React.MouseEvent<HTMLAnchorElement>) => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current)
		}
		positionUnderline(event.currentTarget)
	}

	const handleMouseLeave = () => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current)
		}

		// Set a short delay before repositioning the underline
		hoverTimeoutRef.current = setTimeout(() => {
			const activeElement = getActiveNavElement()
			if (activeElement) {
				positionUnderline(activeElement)
			}
		}, 100)
	}

	return (
		<>
			<div
				className={`${styles.headerPlaceholder} ${
					scrolled ? styles.headerPlaceholderScrolled : ""
				}`}
			/>
			<header
				className={`${styles.header} ${scrolled ? styles.headerScrolled : ""}`}
			>
				<div className={styles.inner}>
					<h1 style={{opacity: headerOpacity, transition: "opacity 0.3s ease"}}>
						<Link to="/">Nate Weiner</Link>
					</h1>
					<ul className={styles.nav} ref={navRef}>
						{sections.map((section) => (
							<li key={section.name}>
								{section.path ? (
									// Regular link for items with a path
									<Link
										to={section.path}
										className={
											!isRootPage && navHighlight === section.name
												? styles.isActive
												: ""
										}
										onMouseEnter={handleMouseEnter}
										onMouseLeave={handleMouseLeave}
									>
										{section.name}
									</Link>
								) : (
									// ScrollLink for items without a path (anchor links)
									<ScrollLink
										name={section.name}
										className={
											isRootPage &&
											activeSection.toLowerCase() === section.name.toLowerCase()
												? styles.isActive
												: ""
										}
										onMouseEnter={handleMouseEnter}
										onMouseLeave={handleMouseLeave}
										onClick={() => {
											// Set active section immediately
											setActiveSection(section.name)
											// Record click time to eliminate underline moving after clicking
											lastClickTimeRef.current = Date.now()
										}}
									/>
								)}
							</li>
						))}
						<div className={styles.activeUnderline} ref={underlineRef} />
					</ul>
					<SocialLinksNav contextClassName={styles.socialLinksNav} />
				</div>
			</header>
		</>
	)
}
